import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { QuicksightService } from './user/quicksight.service';
import { TokenPropertyService } from './token-property.service';

@Injectable({
  providedIn: 'root'
})
export class ChatEligibilityService {

  constructor(
    private quicksightService: QuicksightService,
    private tokenPropertyService: TokenPropertyService
  ) {}

  /**
   * Determines if the user is eligible for chat/AI features based on environment and client code
   * This replaces the individual logic in app.component.ts and should be used by WebSocket service
   */
  isChatEligible(): boolean {
    // Pull configured substrings that are allowed; empty array means unrestricted.
    const userIdSubstrings: string[] = (environment as any).chatAllowedUserIdSubstrings || [];
    // Per requirement we look at custom:userid claim (not email / username). Returns null if absent.
    const customUserId: string | null = this.tokenPropertyService.getPropertyFromToken('custom:userid');
    /* userIdAllowed explanation:
      - If no substrings configured => allow (acts as wildcard)
      - Else we require customUserId to exist AND at least one substring match (case-insensitive)
      - Array.prototype.some() stops iterating after the first match for efficiency */
    const userIdAllowed = userIdSubstrings.length === 0 || (!!customUserId && userIdSubstrings.some(substr => customUserId!.toLowerCase().includes(substr.toLowerCase())));

    // Ensure we have tokens before proceeding
    const hasToken = !!sessionStorage.getItem('id_token');
    
    if (!hasToken) {
      this.logInfo('No token available yet, chat not eligible');
      return false;
    }

    // 1. If chatEnabled is true, show the button unconditionally
    if (environment.chatEnabled) {
      this.logInfo('Chat enabled globally, user is eligible');
      return true;
    }
    
    // 2. If chatEnabled is false, check that BOTH client code AND userId match criteria
    if (Array.isArray(environment.chatClientCodes) && environment.chatClientCodes.length > 0) {
      const clientCode = this.quicksightService.getClientCode();
      const clientCodeAllowed = !!clientCode && environment.chatClientCodes.includes(clientCode);
      const allowed = clientCodeAllowed && userIdAllowed;
      
      this.logInfo('Chat eligibility override check:', {
        clientCode,
        clientCodeAllowed,
        userIdAllowed,
        allowedClientCodes: environment.chatClientCodes,
        finalResult: allowed,
        clientCodeIsNull: clientCode === null,
        clientCodeIsUndefined: clientCode === undefined
      });
      
      return allowed;
    }
    
    // 3. Otherwise, not eligible
('Chat not eligible by default');
    return false;
  }

  /**
   * Get detailed eligibility information for debugging
   */
  getEligibilityDebugInfo(): any {
    const userIdSubstrings: string[] = (environment as any).chatAllowedUserIdSubstrings || [];
    const customUserId: string | null = this.tokenPropertyService.getPropertyFromToken('custom:userid');
    const userIdAllowed = userIdSubstrings.length === 0 || (!!customUserId && userIdSubstrings.some(substr => customUserId!.toLowerCase().includes(substr.toLowerCase())));
    const hasToken = !!sessionStorage.getItem('id_token');
    const clientCode = this.quicksightService.getClientCode();
    const clientCodeAllowed = !!clientCode && environment.chatClientCodes.includes(clientCode);

    return {
      hasToken,
      chatEnabled: environment.chatEnabled,
      chatClientCodes: environment.chatClientCodes,
      userIdSubstrings,
      customUserId,
      userIdAllowed,
      clientCode,
      clientCodeAllowed,
      finalEligibility: this.isChatEligible()
    };
  }
    /**
   * Log helper methods that only log in non-production environments
   */
  private logInfo(message: string, ...data: any[]): void {
    if (!environment.production) {
      console.log(`[ChatEligibilityService] ${message}`, ...data);
    }
  }
}
